// Pure helpers for circle management: invite codes, invite labels, QR paths
// and which member actions the caller may take.
import type { Schemas } from "@10twenty/api";

type Invite = Schemas["Invite"];
type Member = Schemas["Member"];
type Role = Schemas["Role"];

/** The contract's invite code: 8 characters, no 0/O/1/I. */
const INVITE_CODE = /^[A-HJ-NP-Z2-9]{8}$/;

export const isInviteCode = (code: string): boolean => INVITE_CODE.test(code);

/** A bare code as typed: any case, spaces and dashes ignored. Null if invalid. */
export function normalizeInviteCode(input: string): string | null {
  const code = input.replace(/[\s-]/g, "").toUpperCase();
  return isInviteCode(code) ? code : null;
}

export interface ParsedInvite {
  code: string;
  /** The link's origin; null for a bare code (which belongs to this server). */
  origin: string | null;
}

/**
 * What someone typed or pasted into the join form: a bare code, or an http(s)
 * invite link whose path ends in `/join/<code>` (resolved against `base`, so
 * relative and protocol-relative links get their real origin). Callers must
 * refuse links for another origin rather than send their code to this server.
 */
export function parseInviteInput(input: string, base: string): ParsedInvite | null {
  const trimmed = input.trim();
  if (!/[/:]/.test(trimmed)) {
    const code = normalizeInviteCode(trimmed);
    return code ? { code, origin: null } : null;
  }
  let url: URL;
  try {
    url = new URL(trimmed, base);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  const match = /\/join\/([^/]+)\/?$/.exec(url.pathname);
  let code: string | null = null;
  try {
    code = match ? normalizeInviteCode(decodeURIComponent(match[1]!)) : null;
  } catch {
    // Malformed percent-encoding.
  }
  return code ? { code, origin: url.origin } : null;
}

/** A code as shown to people: `K7QX-4M2P`. */
export const formatInviteCode = (code: string): string => `${code.slice(0, 4)}-${code.slice(4)}`;

const HOUR_MS = 3_600_000;

/** "Expires in 3 days" / "Expires in 5 h" / "Expires in 12 min" / "Expired". */
export function expiryText(expiresAt: string, nowMs: number): string {
  const left = Date.parse(expiresAt) - nowMs;
  if (!(left > 0)) return "Expired";
  if (left >= 48 * HOUR_MS) return `Expires in ${Math.floor(left / (24 * HOUR_MS))} days`;
  if (left >= HOUR_MS) return `Expires in ${Math.floor(left / HOUR_MS)} h`;
  return `Expires in ${Math.max(1, Math.floor(left / 60_000))} min`;
}

/** "2 of 5 uses" / "Used once" / "Not used yet". */
export function usesText(invite: Pick<Invite, "uses" | "max_uses">): string {
  if (invite.max_uses != null) return `${invite.uses} of ${invite.max_uses} ${invite.max_uses === 1 ? "use" : "uses"}`;
  if (invite.uses === 0) return "Not used yet";
  return invite.uses === 1 ? "Used once" : `Used ${invite.uses} times`;
}

/**
 * An SVG path drawing the dark modules of a QR matrix (`matrix[y][x]`), one
 * unit per module; horizontal runs share a rectangle.
 */
export function qrPath(matrix: readonly (readonly boolean[])[]): string {
  const parts: string[] = [];
  matrix.forEach((row, y) => {
    let x = 0;
    while (x < row.length) {
      if (!row[x]) {
        x++;
        continue;
      }
      const start = x;
      while (x < row.length && row[x]) x++;
      parts.push(`M${start} ${y}h${x - start}v1h${start - x}z`);
    }
  });
  return parts.join("");
}

export interface MemberActions {
  /** The caller may make this member an admin (never a supervised one). */
  promote: boolean;
  /** The caller may make this admin a plain member (never the circle's last admin). */
  demote: boolean;
  /** The caller may remove this member (admins, for anyone but themselves). */
  remove: boolean;
  /** This is the caller, who may leave unless they are the last admin. */
  leave: boolean;
  /** This member is the circle's only admin. */
  lastAdmin: boolean;
}

/** What the caller (`selfId`, with `selfRole` in the circle) may do to `member`. */
export function memberActions(
  member: Pick<Member, "user_id" | "role" | "supervised">,
  selfId: string,
  selfRole: Role,
  members: readonly Pick<Member, "role">[],
): MemberActions {
  const isSelf = member.user_id === selfId;
  const isAdmin = selfRole === "admin";
  const lastAdmin = member.role === "admin" && members.filter((m) => m.role === "admin").length <= 1;
  return {
    // A supervised member can't be promoted (409 supervised_member).
    promote: isAdmin && member.role === "member" && !member.supervised,
    demote: isAdmin && member.role === "admin" && !lastAdmin,
    remove: isAdmin && !isSelf,
    leave: isSelf && !lastAdmin,
    lastAdmin,
  };
}

/** Admins first, then by name; the caller leads their group. */
export function sortMembers<T extends Pick<Member, "user_id" | "role" | "name">>(
  members: readonly T[],
  selfId: string,
): T[] {
  const rank = (m: T) => (m.role === "admin" ? 0 : 2) + (m.user_id === selfId ? 0 : 1);
  return [...members].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
}

type Circle = Schemas["Circle"];
type CircleKind = Schemas["CircleKind"];

/** A circle's kind as a badge. */
export const KIND_LABELS: Record<CircleKind, string> = { family: "Family", friends: "Friends", event: "Event" };

/** Section titles for circles grouped by kind, in display order. */
const GROUP_TITLES: Record<CircleKind, string> = { family: "Family", friends: "Friends", event: "Events" };
const GROUP_ORDER: readonly CircleKind[] = ["family", "friends", "event"];

export interface CircleGroup<T> {
  kind: CircleKind;
  title: string;
  circles: T[];
}

/**
 * Circles by kind (Family, Friends, Events; empty groups left out). Family and
 * friends circles by name; events not yet over by start, then ended ones,
 * most recent first.
 */
export function groupCircles<T extends Pick<Circle, "kind" | "name" | "event">>(
  circles: readonly T[],
  nowMs: number,
): CircleGroup<T>[] {
  const ended = (c: T) => c.event !== undefined && Date.parse(c.event.ends_at) <= nowMs;
  const byEvent = (a: T, b: T) => {
    if (ended(a) !== ended(b)) return ended(a) ? 1 : -1;
    const at = (c: T, key: "starts_at" | "ends_at") => (c.event ? Date.parse(c.event[key]) : 0);
    return ended(a) ? at(b, "ends_at") - at(a, "ends_at") : at(a, "starts_at") - at(b, "starts_at");
  };
  return GROUP_ORDER.map((kind) => ({
    kind,
    title: GROUP_TITLES[kind],
    circles: circles
      .filter((c) => c.kind === kind)
      .sort((a, b) => (kind === "event" ? byEvent(a, b) : 0) || a.name.localeCompare(b.name)),
  })).filter((group) => group.circles.length > 0);
}

/** "3 of 8 members" with a limit, else "3 members" / "1 member". */
export function memberCountText(circle: Pick<Circle, "member_count" | "member_limit">): string {
  const count = circle.member_count;
  if (circle.member_limit != null) return `${count} of ${circle.member_limit} members`;
  return `${count} ${count === 1 ? "member" : "members"}`;
}

/** The circle has reached (or, after a lowered limit, passed) its member limit and takes nobody new. */
export function isFull(circle: Pick<Circle, "member_count" | "member_limit">): boolean {
  return circle.member_limit != null && circle.member_count >= circle.member_limit;
}

/** "This circle is full" / "This event is full". */
export function fullText(kind: CircleKind): string {
  return kind === "event" ? "This event is full" : "This circle is full";
}

/** "Free for up to 8 people", or null without a limit. */
export function freeLimitText(limit: number | null | undefined): string | null {
  return limit == null ? null : `Free for up to ${limit} people`;
}

/** What a new circle of each kind means for its members, for the create form. */
export const KIND_DESCRIPTIONS: Record<"family" | "friends", string> = {
  family: "Members see each other's exact location, and an SOS always reaches everyone.",
  friends:
    "Members join sharing an area of about 2 km instead of their exact location, and choose whether their SOS reaches the circle.",
};

/** What switching a circle to `to` changes for its members, for the admin's confirmation. */
export function kindSwitchText(to: "family" | "friends"): string {
  return to === "friends"
    ? "People who join from now on start by sharing an area of about 2 km instead of their exact location. " +
        "Current members keep sharing as they do now. Check-in alerts are off unless someone turns them on. " +
        'Everyone\'s SOS still reaches the circle; each member can turn that off with "Send my SOS here".'
    : "People who join from now on share their exact location. Anyone sharing an approximate area keeps doing so " +
        "until they turn it off. Check-in alerts are on unless someone turns them off, and every member's SOS " +
        "reaches the circle.";
}

/** Circles an API token may be created for: never event circles. */
export function tokenCircles<T extends Pick<Circle, "kind">>(circles: readonly T[]): T[] {
  return circles.filter((circle) => circle.kind !== "event");
}
